// =============================================================================
// VandieZ admin-web -- Driver payouts (/payouts)
// =============================================================================
// Two tabs:
//   * "Candidates" — drivers with positive wallet balance + has_primary_bank.
//     Initiate a payout against their primary bank account.
//   * "History"    — paginated payout list with status pills + per-row
//                    transition buttons (Processing / Complete / Fail).
//
// Initiation flow:
//   Click "Initiate payout" on a candidate row → modal asks for amount
//   (defaults to full balance) + optional notes → POST /admin/payouts →
//   row refreshes (balance drops, payout appears in History).
//
// Transitions (DR-25 / AD-13, migration 0097 -- the approval gate):
//   pending    → "Approve" / "Hold" / "Mark failed"
//   approved   → "Mark processing" / "Mark completed" / "Hold" / "Mark failed"
//   processing → "Mark completed" / "Hold" / "Mark failed"
//   hold       → "Release hold" (back to PENDING, not approved)
//
//   `Approve` is a gate, not a label: the backend refuses /processing and
//   /complete on anything that is not `approved`, so money cannot leave
//   without a human having authorised it. `Hold` is distinct from `Fail` --
//   failing writes a refund ledger row, so using it to freeze a payout
//   during a dispute would make the ledger state something untrue.
//
//   Failed transitions require a `failed_reason`; holds require a `reason`.
// =============================================================================

import {
  AlertCircle,
  CheckCircle2,
  PauseCircle,
  Download,
  Loader2,
  RefreshCw,
  Send,
  ShieldCheck,
  Wallet,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";


import { payoutsAdminApi, streamCsvDownload } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  AdminBankAccountRead,
  AdminDriverWithBalanceEntry,
  AdminPayoutRead,
  PayoutStatus,
} from "@/types/payouts";

const PAYOUT_STATUS_STYLES: Record<PayoutStatus, string> = {
  pending: "bg-warning-subtle text-warning-subtle-foreground",
  approved: "bg-primary-subtle text-primary-subtle-foreground",
  hold: "bg-danger-subtle text-danger-subtle-foreground",
  processing: "bg-primary-subtle text-primary-subtle-foreground",
  completed: "bg-success-subtle text-success-subtle-foreground",
  failed: "bg-danger-subtle text-danger-subtle-foreground",
};

const PAYOUT_STATUS_LABEL: Record<PayoutStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  hold: "On hold",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
};

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function StatusPill({ status }: { status: PayoutStatus }): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        PAYOUT_STATUS_STYLES[status],
      )}
    >
      {PAYOUT_STATUS_LABEL[status]}
    </span>
  );
}

interface InitiatePayoutModalProps {
  candidate: AdminDriverWithBalanceEntry;
  onClose: () => void;
  onSuccess: () => void;
}

function InitiatePayoutModal({
  candidate,
  onClose,
  onSuccess,
}: InitiatePayoutModalProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [bankAccount, setBankAccount] = useState<AdminBankAccountRead | null>(
    null,
  );
  const [amount, setAmount] = useState<string>(candidate.balance);
  const [notes, setNotes] = useState<string>("");
  const [bankLoading, setBankLoading] = useState<boolean>(true);
  const [bankError, setBankError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the driver's primary bank account when the modal opens.
  // The admin shouldn't have to paste a UUID — we look it up server-side
  // and just show last-4 + IFSC so they can sanity-check the destination.
  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    const controller = new AbortController();
    setBankLoading(true);
    setBankError(null);
    payoutsAdminApi
      .driverBankAccounts(accessToken, candidate.user_id, controller.signal)
      .then((res) => {
        const primary =
          res.items.find((a) => a.is_primary) ?? res.items[0] ?? null;
        setBankAccount(primary);
        if (primary === null) {
          setBankError(
            "This driver has no bank account on file. Ask them to add one before initiating a payout.",
          );
        }
      })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setBankError(msg);
      })
      .finally(() => { setBankLoading(false); });
    return () => { controller.abort(); };
  }, [accessToken, candidate.user_id]);

  const submit = async () => {
    if (accessToken === null) {
      return;
    }
    if (bankAccount === null) {
      setError("No bank account on file for this driver.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await payoutsAdminApi.initiate(accessToken, {
        driver_user_id: candidate.user_id,
        bank_account_id: bankAccount.id,
        amount,
        ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}),
      });
      onSuccess();
      onClose();
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Initiate payout</CardTitle>
          <CardDescription>
            {candidate.full_name} ·{" "}
            <span className="font-mono">{candidate.phone_masked}</span>
            <br />
            Current balance: ₹{candidate.balance}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Bank account</Label>
            {bankLoading ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading driver's bank account…
              </div>
            ) : bankAccount !== null ? (
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{bankAccount.bank_name}</span>
                  {bankAccount.is_primary && (
                    <span className="rounded-full bg-success-subtle px-2 py-0.5 text-xs text-success-subtle-foreground">
                      Primary
                    </span>
                  )}
                </div>
                <div className="mt-1 font-mono text-xs text-muted-foreground">
                  •••• {bankAccount.account_number_last_4} ·{" "}
                  {bankAccount.ifsc_code}
                </div>
                {bankAccount.branch_name !== null && (
                  <div className="text-xs text-muted-foreground">
                    {bankAccount.branch_name}
                  </div>
                )}
                {!bankAccount.verified && (
                  <div className="mt-1 text-xs text-warning-subtle-foreground">
                    Not verified yet — proceed only if you have an offline
                    confirmation of the account.
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border border-danger bg-danger-subtle px-3 py-2 text-sm text-danger-subtle-foreground">
                {bankError ?? "No bank account found."}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="payout-amount">Amount (INR)</Label>
            <Input
              id="payout-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); }}
              disabled={submitting || bankAccount === null}
            />
          </div>
          <div>
            <Label htmlFor="payout-notes">Notes (optional)</Label>
            <Textarea
              id="payout-notes"
              value={notes}
              onChange={(e) => { setNotes(e.target.value); }}
              maxLength={500}
              disabled={submitting || bankAccount === null}
              placeholder="Visible to admin only"
            />
          </div>
          {error !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={submitting || bankLoading || bankAccount === null}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Initiate"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface TransitionRowProps {
  payout: AdminPayoutRead;
  onTransitioned: () => void;
}

function TransitionRow({
  payout,
  onTransitioned,
}: TransitionRowProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [pendingAction, setPendingAction] = useState<
    "approve" | "hold" | "release" | "processing" | "complete" | "fail" | null
  >(null);
  const [reference, setReference] = useState<string>(payout.reference ?? "");
  const [failedReason, setFailedReason] = useState<string>("");
  const [holdReason, setHoldReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = () => {
    setPendingAction(null);
    setError(null);
  };

  const submit = async () => {
    if (accessToken === null || pendingAction === null) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        ...(reference.trim().length > 0
          ? { reference: reference.trim() }
          : {}),
        ...(failedReason.trim().length > 0
          ? { failed_reason: failedReason.trim() }
          : {}),
      };
      if (pendingAction === "approve") {
        await payoutsAdminApi.approvePayout(accessToken, payout.id, {});
      } else if (pendingAction === "hold") {
        await payoutsAdminApi.holdPayout(accessToken, payout.id, {
          reason: holdReason.trim(),
        });
      } else if (pendingAction === "release") {
        await payoutsAdminApi.releasePayoutHold(accessToken, payout.id);
      } else if (pendingAction === "processing") {
        await payoutsAdminApi.markProcessing(accessToken, payout.id, body);
      } else if (pendingAction === "complete") {
        await payoutsAdminApi.markComplete(accessToken, payout.id, body);
      } else {
        await payoutsAdminApi.markFailed(accessToken, payout.id, body);
      }
      onTransitioned();
      setPendingAction(null);
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isTerminal =
    payout.status === "completed" || payout.status === "failed";

  if (isTerminal) {
    return <span className="text-xs text-muted-foreground">No actions</span>;
  }

  if (pendingAction === null) {
    return (
      <div className="flex flex-wrap gap-1">
        {payout.status === "hold" ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setPendingAction("release"); }}
          >
            Release hold
          </Button>
        ) : (
          <>
            {payout.status === "pending" && (
              <Button
                size="sm"
                variant="default"
                onClick={() => { setPendingAction("approve"); }}
              >
                <ShieldCheck className="mr-1 h-3 w-3" /> Approve
              </Button>
            )}
            {payout.status === "approved" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setPendingAction("processing"); }}
              >
                Processing
              </Button>
            )}
            {/* Complete is offered only from `approved`/`processing`. The
                backend refuses it from `pending`, and showing a button that
                always 409s is worse than showing none. */}
            {(payout.status === "approved" ||
              payout.status === "processing") && (
              <Button
                size="sm"
                variant="default"
                onClick={() => { setPendingAction("complete"); }}
              >
                <CheckCircle2 className="mr-1 h-3 w-3" /> Complete
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setPendingAction("hold"); }}
            >
              <PauseCircle className="mr-1 h-3 w-3" /> Hold
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => { setPendingAction("fail"); }}
            >
              <XCircle className="mr-1 h-3 w-3" /> Fail
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded border bg-muted/40 p-2">
      <div className="text-xs font-medium capitalize">
        Confirm: mark {pendingAction}
      </div>
      {pendingAction === "hold" && (
        <Textarea
          placeholder="Why is this payout frozen? — required"
          value={holdReason}
          onChange={(e) => { setHoldReason(e.target.value); }}
          maxLength={500}
          disabled={submitting}
        />
      )}
      {(pendingAction === "processing" || pendingAction === "complete") && (
        <Input
          placeholder="Gateway / bank reference (optional)"
          value={reference}
          onChange={(e) => { setReference(e.target.value); }}
          disabled={submitting}
        />
      )}
      {pendingAction === "fail" && (
        <Textarea
          placeholder="Reason — required"
          value={failedReason}
          onChange={(e) => { setFailedReason(e.target.value); }}
          maxLength={500}
          disabled={submitting}
        />
      )}
      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex justify-end gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={cancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          variant={pendingAction === "fail" ? "destructive" : "default"}
          onClick={() => void submit()}
          disabled={submitting}
        >
          {submitting ? "…" : "Confirm"}
        </Button>
      </div>
    </div>
  );
}

export function PayoutsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [tab, setTab] = useState<"candidates" | "history">("candidates");
  const [candidates, setCandidates] = useState<AdminDriverWithBalanceEntry[]>(
    [],
  );
  const [payouts, setPayouts] = useState<AdminPayoutRead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [modalCandidate, setModalCandidate] =
    useState<AdminDriverWithBalanceEntry | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportingAll, setExportingAll] = useState<boolean>(false);
  const exportToast = useToast();
  // History tab has no Prev/Next pagination yet (newest-first slice only).
  // The page-size selector controls how big a slice the operator pulls.
  const [historyPageSize, setHistoryPageSize] = useState<number>(50);

  const loadCandidates = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await payoutsAdminApi.candidates(accessToken, signal);
        setCandidates(res.items);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  const loadHistory = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await payoutsAdminApi.list(
          accessToken,
          { limit: historyPageSize, offset: 0 },
          signal,
        );
        setPayouts(res.items);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, historyPageSize],
  );

  useEffect(() => {
    const controller = new AbortController();
    if (tab === "candidates") {
      void loadCandidates(controller.signal);
    } else {
      void loadHistory(controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [tab, loadCandidates, loadHistory]);

  const refresh = () => {
    if (tab === "candidates") {
      void loadCandidates();
    } else {
      void loadHistory();
    }
  };

  // Streaming export — every payout matching the current filter set
  // (only applicable to the History tab; candidates have no equivalent
  // streaming endpoint).
  const exportAllCsv = async (): Promise<void> => {
    if (accessToken === null || tab !== "history") {return;}
    setExportingAll(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await streamCsvDownload(
        accessToken,
        "/admin/exports/payouts.csv",
        `vandiez-payouts-${stamp}.csv`,
      );
      exportToast.success("Export started — check your downloads.");
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Export failed.";
      exportToast.error(msg);
    } finally {
      setExportingAll(false);
    }
  };

  // Export CSV mirrors whichever tab is active. Candidates → drivers
  // with positive balance; History → payout audit trail.
  const exportCsv = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setExporting(true);
    try {
      const escape = (v: string): string =>
        /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      let header: string[];
      let rows: (string | number)[][];
      let filenameTab: string;
      if (tab === "candidates") {
        const res = await payoutsAdminApi.candidates(accessToken);
        header = ["driver", "phone", "balance", "has_primary_bank"];
        rows = res.items.map((c) => [
          c.full_name,
          c.phone_masked,
          c.balance,
          c.has_primary_bank ? "yes" : "no",
        ]);
        filenameTab = "candidates";
      } else {
        const res = await payoutsAdminApi.list(accessToken, {
          limit: 200,
          offset: 0,
        });
        header = [
          "initiated_at",
          "driver_name",
          "driver_phone",
          "amount",
          "status",
          "reference",
          "failed_reason",
          "completed_at",
        ];
        rows = res.items.map((p) => [
          p.initiated_at,
          p.driver_name ?? "",
          p.driver_phone ?? "",
          p.amount,
          p.status,
          p.reference ?? "",
          p.failed_reason ?? "",
          p.completed_at ?? "",
        ]);
        filenameTab = "payouts";
      }
      const csv = [
        header.join(","),
        ...rows.map((r) => r.map((c) => escape(String(c))).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vandiez-${filenameTab}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const exportRowCount = tab === "candidates" ? candidates.length : payouts.length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Driver payouts"
        description="Initiate payouts for drivers with positive wallet balance, then transition them through processing → completed (or failed)."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void exportCsv()}
              disabled={exporting || exportRowCount === 0}
              title="Current tab's visible rows (~200 cap)"
            >
              <Download className="mr-1 h-4 w-4" />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            {tab === "history" && (
              <Button
                variant="outline"
                onClick={() => void exportAllCsv()}
                disabled={exportingAll}
                title="Stream every payout (unlimited)"
              >
                <Download className="mr-1 h-4 w-4" />
                {exportingAll ? "Streaming…" : "Export all"}
              </Button>
            )}
          </div>
        }
      />
        <div className="flex items-center justify-between">
          <div className="inline-flex gap-1 rounded-md border bg-background p-1">
            <button
              className={cn(
                "rounded px-3 py-1 text-sm",
                tab === "candidates"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )}
              onClick={() => { setTab("candidates"); }}
            >
              <Wallet className="mr-1 inline h-4 w-4" /> Candidates
            </button>
            <button
              className={cn(
                "rounded px-3 py-1 text-sm",
                tab === "history"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )}
              onClick={() => { setTab("history"); }}
            >
              <Send className="mr-1 inline h-4 w-4" /> History
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
        </div>

        {flash !== null && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>{flash}</AlertDescription>
          </Alert>
        )}
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {tab === "candidates" ? (
          <Card>
            <CardHeader>
              <CardTitle>Drivers with positive balance</CardTitle>
              <CardDescription>
                Their wallet balance covers a payout. Click "Initiate" to send
                money out.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading && candidates.length === 0 ? (
                <TableSkeleton columns={5} />
              ) : candidates.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground">
                  No drivers have a positive wallet balance right now.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="py-2 pr-4">Driver</th>
                        <th className="py-2 pr-4">Phone</th>
                        <th className="py-2 pr-4">Balance</th>
                        <th className="py-2 pr-4">Bank</th>
                        <th className="py-2 pr-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((c) => (
                        <tr key={c.user_id} className="border-b last:border-b-0">
                          <td className="py-3 pr-4 font-medium">
                            {c.full_name}
                          </td>
                          <td className="py-3 pr-4 font-mono text-xs">
                            {c.phone_masked}
                          </td>
                          <td className="py-3 pr-4 font-mono">
                            ₹{c.balance}
                          </td>
                          <td className="py-3 pr-4">
                            {c.has_primary_bank ? (
                              <span className="rounded-full bg-success-subtle px-2 py-0.5 text-xs text-success-subtle-foreground">
                                On file
                              </span>
                            ) : (
                              <span className="rounded-full bg-danger-subtle px-2 py-0.5 text-xs text-danger-subtle-foreground">
                                Missing
                              </span>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            <Button
                              size="sm"
                              disabled={!c.has_primary_bank}
                              onClick={() => { setModalCandidate(c); }}
                            >
                              <Send className="mr-1 h-3 w-3" /> Initiate
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle>Payout history</CardTitle>
                  <CardDescription>
                    Newest first. Transition buttons appear inline for
                    non-terminal rows.
                  </CardDescription>
                </div>
                <label className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  Rows:
                  <select
                    value={historyPageSize}
                    onChange={(e) => {
                      setHistoryPageSize(Number.parseInt(e.target.value, 10));
                    }}
                    className="rounded border bg-background px-1 py-0.5 text-xs"
                  >
                    {[50, 100, 200].map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              </div>
            </CardHeader>
            <CardContent>
              {loading && payouts.length === 0 ? (
                <TableSkeleton columns={6} />
              ) : payouts.length === 0 ? (
                <div className="py-10 text-center text-muted-foreground">
                  No payouts have been initiated yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="py-2 pr-4">Initiated</th>
                        <th className="py-2 pr-4">Driver</th>
                        <th className="py-2 pr-4">Amount</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2 pr-4">Reference</th>
                        <th className="py-2 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p) => (
                        <tr key={p.id} className="border-b last:border-b-0 align-top">
                          <td className="py-3 pr-4 text-xs text-muted-foreground">
                            {formatDateTime(p.initiated_at)}
                          </td>
                          <td className="py-3 pr-4">
                            <div className="font-medium">
                              {p.driver_name ?? "—"}
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {p.driver_phone ?? `${p.driver_user_id.slice(0, 8)}…`}
                            </div>
                          </td>
                          <td className="py-3 pr-4 font-mono">₹{p.amount}</td>
                          <td className="py-3 pr-4">
                            <StatusPill status={p.status} />
                            {p.failed_reason !== null && (
                              <div className="mt-1 text-xs text-danger-subtle-foreground">
                                {p.failed_reason}
                              </div>
                            )}
                          </td>
                          <td className="py-3 pr-4 font-mono text-xs">
                            {p.reference ?? "—"}
                          </td>
                          <td className="py-3 pr-4">
                            <TransitionRow
                              payout={p}
                              onTransitioned={() => {
                                setFlash(`Payout ${p.id.slice(0, 8)}… updated.`);
                                void loadHistory();
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      {modalCandidate !== null && (
        <InitiatePayoutModal
          candidate={modalCandidate}
          onClose={() => { setModalCandidate(null); }}
          onSuccess={() => {
            setFlash(`Payout initiated for ${modalCandidate.full_name}.`);
            void loadCandidates();
          }}
        />
      )}
    </div>
  );
}
